import IosSwitch from "components/shared/IosSwitch";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface TProps {
    title?: string;
    description?: string;
    /** Whether the feature is currently enabled (persisted in backend) */
    enabled: boolean;
    /** Text value currently persisted in the backend */
    persistedValue: string;
    /** Called when the user toggles the switch — parent persists to backend */
    onToggle: (enabled: boolean) => void;
    /** Called when the user saves new text content */
    onSave: (value: string) => void;
    rows?: number;
    maxLength?: number;
    /** When provided, controls textarea visibility independently of `enabled` */
    expanded?: boolean;
}

const SettingToggleTextarea = ({
    title,
    description,
    enabled,
    persistedValue,
    onToggle,
    onSave,
    rows = 5,
    maxLength = 500,
    expanded,
}: TProps) => {
    const [t] = useTranslation();
    const [localValue, setLocalValue] = useState(persistedValue);

    useEffect(() => {
        setLocalValue(persistedValue);
    }, [persistedValue]);

    const isSaveDisabled = localValue.trim() === "" || localValue === persistedValue;
    const showTextarea = expanded !== undefined ? expanded : enabled;

    return (
        <div className="w-full flex flex-col gap-3">
            <div className="w-full flex items-center justify-between gap-4">
                <div className="flex flex-col gap-0.5">
                    {title && <span className="text-sm">{title}</span>}
                    {description && <span className="text-xs text-gray-500 whitespace-pre-wrap">{description}</span>}
                </div>
                <IosSwitch
                    size="medium"
                    checked={enabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggle(e.target.checked)}
                />
            </div>
            {showTextarea && (
                <div className="flex flex-col gap-2">
                    <StyledTextField
                        multiline
                        rows={rows}
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        inputProps={{ maxLength }}
                        fullWidth
                        size="small"
                    />
                    <div className="flex justify-end">
                        <StyledButton
                            buttonType="primary"
                            disabled={isSaveDisabled}
                            onClick={() => onSave(localValue)}
                            size="small"
                        >
                            {t("Common.Save")}
                        </StyledButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingToggleTextarea;
